<script>
import BranchAdminDialog from '@/views/branches/BranchAdminDialog.vue'
import {
  getBranchList,
  getBranchDetail,
  deleteBranchAdmin,
  updateBranchAdminPassword,
} from '@/api/branch'
import { getAdminInfo } from '@/utils/token'

/**
 * 본사 관리자 관리 페이지.
 *
 * 본사는 branch.branch_type = 'HQ' 인 시스템 기본 지점으로 관리하고,
 * 본사 관리자는 해당 HQ branch_code에 연결된 branch_admin 계정으로 저장한다.
 */
export default {
  name: 'HqAdminListPage',
  components: { BranchAdminDialog },
  data() {
    return {
      currentAdmin: getAdminInfo(),
      loading: true,
      errorMsg: '',
      hqBranch: null,
      hqBranches: [],

      adminDialog: false,
      adminFormMode: 'create',
      selectedAdmin: null,

      adminDeleteDialog: false,
      adminDeleteLoading: false,
      adminToDelete: null,

      pwdDialog: false,
      pwdSaving: false,
      pwdForm: { newPassword: '', confirmPassword: '' },
      pwdTarget: null,
      pwdError: '',
      pwdMsg: '',
    }
  },
  computed: {
    isHq() {
      return this.currentAdmin?.adminType === 'HQ'
    },
    hqBranchCode() {
      return this.hqBranch?.branchCode || ''
    },
    admins() {
      return this.hqBranch?.admins || []
    },
    canDeleteHqAdmin() {
      return this.admins.length > 1
    },
  },
  mounted() {
    this.fetchHqBranch()
  },
  methods: {
    async fetchHqBranch() {
      this.loading = true
      this.errorMsg = ''
      try {
        const listRes = await getBranchList({
          branchType: 'HQ',
          branchStatus: undefined,
          page: 1,
          size: 10,
        })

        if (listRes.data?.code !== 0) {
          this.errorMsg = listRes.data?.message || '본사 정보를 조회하지 못했습니다.'
          this.hqBranch = null
          return
        }

        this.hqBranches = listRes.data?.data?.items || []
        const hq = this.hqBranches[0]
        if (!hq?.branchCode) {
          this.errorMsg = '등록된 본사 정보가 없습니다. 먼저 시스템 기본 본사 지점이 필요합니다.'
          this.hqBranch = null
          return
        }

        const detailRes = await getBranchDetail(hq.branchCode)
        if (detailRes.data?.code !== 0) {
          this.errorMsg = detailRes.data?.message || '본사 상세 정보를 조회하지 못했습니다.'
          this.hqBranch = null
          return
        }

        this.hqBranch = detailRes.data
      } catch (e) {
        this.errorMsg = e?.response?.data?.message || '서버 오류로 본사 관리자 목록을 불러오지 못했습니다.'
        this.hqBranch = null
      } finally {
        this.loading = false
      }
    },

    openAdminCreate() {
      this.adminFormMode = 'create'
      this.selectedAdmin = null
      this.adminDialog = true
    },

    openAdminEdit(admin) {
      this.adminFormMode = 'edit'
      this.selectedAdmin = admin
      this.adminDialog = true
    },

    async onAdminSaved() {
      await this.fetchHqBranch()
    },

    openAdminDelete(admin) {
      if (!this.canDeleteHqAdmin) return
      this.adminToDelete = admin
      this.adminDeleteDialog = true
    },

    async handleAdminDelete() {
      if (!this.hqBranchCode || !this.adminToDelete?.branchAdminCode) return
      if (!this.canDeleteHqAdmin) {
        this.errorMsg = '본사 관리자는 최소 1명 이상 필요합니다.'
        this.adminDeleteDialog = false
        return
      }

      this.adminDeleteLoading = true
      try {
        const res = await deleteBranchAdmin(this.hqBranchCode, this.adminToDelete.branchAdminCode)
        if (res.data?.code === 0) {
          this.adminDeleteDialog = false
          await this.fetchHqBranch()
        } else {
          this.errorMsg = res.data?.message || '관리자 삭제에 실패했습니다.'
        }
      } catch (e) {
        this.errorMsg = e?.response?.data?.message || '서버 오류로 관리자 삭제에 실패했습니다.'
      } finally {
        this.adminDeleteLoading = false
      }
    },

    openPwdChange(admin) {
      this.pwdTarget = admin
      this.pwdForm = { newPassword: '', confirmPassword: '' }
      this.pwdError = ''
      this.pwdMsg = ''
      this.pwdDialog = true
    },

    async handlePwdSave() {
      this.pwdError = ''
      this.pwdMsg = ''

      if (!this.pwdForm.newPassword || this.pwdForm.newPassword.length < 8) {
        this.pwdError = '비밀번호는 8자 이상이어야 합니다.'
        return
      }
      if (this.pwdForm.newPassword !== this.pwdForm.confirmPassword) {
        this.pwdError = '새 비밀번호와 확인 비밀번호가 일치하지 않습니다.'
        return
      }
      if (!this.hqBranchCode || !this.pwdTarget?.branchAdminCode) {
        this.pwdError = '비밀번호를 변경할 관리자 정보가 없습니다.'
        return
      }

      this.pwdSaving = true
      try {
        const res = await updateBranchAdminPassword(
          this.hqBranchCode,
          this.pwdTarget.branchAdminCode,
          { newPassword: this.pwdForm.newPassword }
        )
        if (res.data?.code === 0) {
          this.pwdMsg = '비밀번호가 변경되었습니다.'
          setTimeout(() => { this.pwdDialog = false }, 1200)
        } else {
          this.pwdError = res.data?.message || '비밀번호 변경에 실패했습니다.'
        }
      } catch (e) {
        this.pwdError = e?.response?.data?.message || '서버 오류가 발생했습니다.'
      } finally {
        this.pwdSaving = false
      }
    },

    goBranchDetail() {
      if (this.hqBranchCode) this.$router.push(`/branches/${this.hqBranchCode}`)
    },
  },
}
</script>

<template>
  <div class="hq-admin-page">
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="text-h5 font-weight-bold">본사관리자 관리</div>
        <div class="text-body-2 text-grey mt-1">본사 권한으로 관리자 계정을 추가하고 관리합니다.</div>
      </div>
      <v-btn
        v-if="hqBranch"
        color="primary"
        variant="flat"
        rounded="lg"
        prepend-icon="mdi-plus"
        @click="openAdminCreate"
      >
        본사 관리자 추가
      </v-btn>
    </div>

    <v-alert
      v-if="hqBranches.length > 1"
      type="warning"
      variant="tonal"
      rounded="lg"
      class="mb-4"
    >
      본사 정보가 {{ hqBranches.length }}개 조회되었습니다. 현재 화면은 첫 번째 본사 기준으로 관리자 목록을 표시합니다.
    </v-alert>

    <v-alert
      v-if="errorMsg"
      type="error"
      variant="tonal"
      rounded="lg"
      class="mb-4"
    >
      {{ errorMsg }}
    </v-alert>

    <v-card rounded="lg" elevation="0" border class="mb-5">
      <div v-if="loading" class="d-flex justify-center align-center loading-wrap">
        <v-progress-circular indeterminate color="primary" size="44" />
      </div>

      <template v-else-if="hqBranch">
        <div class="pa-5 d-flex align-center ga-4 flex-wrap">
          <v-avatar color="purple-lighten-4" rounded="lg" size="48">
            <v-icon color="purple" size="26">mdi-domain</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="text-subtitle-1 font-weight-bold">{{ hqBranch.branchName }}</div>
            <div class="text-caption text-medium-emphasis">
              본사코드 {{ hqBranch.branchCode }} · 관리자 {{ admins.length }}명
            </div>
          </div>
          <v-btn variant="tonal" color="purple" rounded="lg" @click="goBranchDetail">
            본사 상세 보기
          </v-btn>
        </div>

        <v-divider />

        <div v-if="!admins.length" class="empty-wrap">
          <v-icon size="42" color="grey-lighten-2" class="mb-3">mdi-account-off-outline</v-icon>
          <div class="text-body-2 text-medium-emphasis">등록된 본사 관리자가 없습니다.</div>
        </div>

        <template v-else>
          <div
            v-if="!canDeleteHqAdmin"
            class="px-5 py-3 text-caption text-medium-emphasis d-flex align-center ga-1"
          >
            <v-icon size="14" color="warning">mdi-shield-lock-outline</v-icon>
            본사 관리자는 최소 1명 이상 필요하므로 마지막 관리자는 삭제할 수 없습니다.
          </div>
          <v-divider v-if="!canDeleteHqAdmin" />

          <v-table density="comfortable" class="admin-table">
            <thead>
              <tr>
                <th class="text-caption font-weight-bold">이름</th>
                <th class="text-caption font-weight-bold">이메일</th>
                <th class="text-caption font-weight-bold">직책</th>
                <th class="text-caption font-weight-bold">전화번호</th>
                <th class="text-caption font-weight-bold text-center" style="width: 190px">관리</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="admin in admins" :key="admin.branchAdminCode">
                <td>
                  <div class="d-flex align-center ga-2">
                    <v-avatar size="28" color="purple-lighten-4" rounded="lg">
                      <span class="text-caption font-weight-bold text-purple">
                        {{ admin.branchAdminName?.charAt(0) }}
                      </span>
                    </v-avatar>
                    <span class="font-weight-medium text-body-2">{{ admin.branchAdminName }}</span>
                  </div>
                </td>
                <td class="text-caption text-medium-emphasis">{{ admin.branchAdminEmail }}</td>
                <td>
                  <v-chip v-if="admin.branchAdminPosition" size="x-small" variant="tonal" color="purple" label>
                    {{ admin.branchAdminPosition }}
                  </v-chip>
                  <span v-else class="text-caption text-disabled">-</span>
                </td>
                <td class="text-caption text-medium-emphasis">{{ admin.branchAdminPhone || '-' }}</td>
                <td class="text-center">
                  <v-btn size="x-small" variant="text" color="primary" class="mr-1" @click="openAdminEdit(admin)">수정</v-btn>
                  <v-btn size="x-small" variant="text" color="purple" class="mr-1" @click="openPwdChange(admin)">비밀번호</v-btn>
                  <v-btn
                    size="x-small"
                    variant="text"
                    color="error"
                    :disabled="!canDeleteHqAdmin"
                    @click="openAdminDelete(admin)"
                  >삭제</v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
        </template>
      </template>

      <div v-else class="empty-wrap">
        <v-icon size="42" color="grey-lighten-2" class="mb-3">mdi-domain-off</v-icon>
        <div class="text-body-2 text-medium-emphasis">본사 정보가 없어 본사 관리자를 추가할 수 없습니다.</div>
      </div>
    </v-card>

    <BranchAdminDialog
      v-if="hqBranchCode"
      v-model="adminDialog"
      :mode="adminFormMode"
      :branch-code="hqBranchCode"
      :admin="selectedAdmin"
      @saved="onAdminSaved"
    />

    <v-dialog v-model="adminDeleteDialog" max-width="380">
      <v-card rounded="lg">
        <v-card-item class="pa-5 pb-3">
          <template #prepend>
            <v-icon color="error">mdi-account-remove-outline</v-icon>
          </template>
          <v-card-title class="text-subtitle-1 font-weight-bold">본사 관리자 삭제</v-card-title>
        </v-card-item>
        <v-card-text class="px-5 pb-3">
          <span class="font-weight-bold">{{ adminToDelete?.branchAdminName }}</span> 관리자를 삭제하시겠습니까?
          <div class="text-caption text-medium-emphasis mt-1">삭제된 계정은 본사 관리자로 로그인할 수 없습니다.</div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" rounded="lg" :disabled="adminDeleteLoading" @click="adminDeleteDialog = false">취소</v-btn>
          <v-btn color="error" variant="flat" rounded="lg" :loading="adminDeleteLoading" @click="handleAdminDelete">삭제</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pwdDialog" max-width="420" persistent>
      <v-card rounded="lg">
        <v-card-item class="pa-5 pb-3">
          <template #prepend>
            <v-icon color="purple">mdi-lock-reset</v-icon>
          </template>
          <v-card-title class="text-subtitle-1 font-weight-bold">비밀번호 변경</v-card-title>
          <v-card-subtitle v-if="pwdTarget">{{ pwdTarget.branchAdminName }} 관리자</v-card-subtitle>
        </v-card-item>
        <v-divider />
        <v-card-text class="pa-5">
          <v-alert v-if="pwdMsg" type="success" variant="tonal" density="compact" rounded="lg" class="mb-4">
            {{ pwdMsg }}
          </v-alert>
          <v-alert v-if="pwdError" type="error" variant="tonal" density="compact" rounded="lg" class="mb-4">
            {{ pwdError }}
          </v-alert>
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="pwdForm.newPassword"
                label="새 비밀번호 *"
                type="password"
                variant="outlined"
                density="compact"
                hint="8자 이상 입력해 주세요"
                persistent-hint
                autocomplete="new-password"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="pwdForm.confirmPassword"
                label="새 비밀번호 확인 *"
                type="password"
                variant="outlined"
                density="compact"
                hide-details
                autocomplete="new-password"
                @keyup.enter="handlePwdSave"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" rounded="lg" :disabled="pwdSaving" @click="pwdDialog = false">취소</v-btn>
          <v-btn color="purple" variant="flat" rounded="lg" :loading="pwdSaving" @click="handlePwdSave">변경</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.loading-wrap,
.empty-wrap {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
}

.admin-table :deep(thead th) {
  background-color: #fafafa;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
}

.admin-table :deep(tbody tr:hover) {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>
